import React from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { setIconFillColor } from '../../styles/mixins';
import HBClickable from '../HBClickable/HBClickable';
import HBText from '../HBText/HBText';
import { INewTableColumn, TSort } from './HBNewTable.types';
import {
    ArrowLineDownAlt8,
    ArrowLineUpAlt8,
    InfoCircle16
} from '@honeybook/icons';
import HBTooltip from '../HBTooltip/HBTooltip';

interface Props<T> {
    column: INewTableColumn<T>;
    onSortChange?: (columnId: string) => void;
    sort?: TSort;
}

const HBNewTableHeader = <T,>({
    column,
    onSortChange,
    sort = 'none'
}: Props<T>) => {
    const hasSort = sort !== 'none';

    return (
        <Container
            onClick={() => {
                if (column.isSortable) {
                    onSortChange(column.id);
                }
            }}
            isDisabled={!column.isSortable}
            testIdName="header-table"
        >
            <HBText type="subtitle12" color="text.tertiary">
                {column.title}
            </HBText>
            {!!column.headerTooltip && (
                <HBTooltip
                    target={<InfoCircle16 />}
                    targetContainerStyle={{
                        marginBottom: '-6px',
                        marginLeft: '8px'
                    }}
                    placement="top"
                    offsetY={5}
                >
                    {column.headerTooltip}
                </HBTooltip>
            )}
            {column.isSortable && (
                <HBTooltip
                    target={
                        <ArrowWrapper $hasSort={hasSort}>
                            <StyledArrowLineUpAlt8
                                isDescending={sort === 'descending'}
                            />
                            <StyledArrowLineDownAlt8
                                isDescending={sort === 'descending'}
                            />
                        </ArrowWrapper>
                    }
                    placement="top"
                    offsetY={5}
                >
                    {column.sortTooltip}
                </HBTooltip>
            )}
        </Container>
    );
};

export default HBNewTableHeader;

// --- Styles ---

const StyledArrowLineUpAlt8 = styled(ArrowLineUpAlt8)<{
    isDescending: boolean;
}>`
    position: absolute;
    top: -3px;
    ${props => (props.isDescending ? 'left: 0px' : 'right: 0px')};
`;

const StyledArrowLineDownAlt8 = styled(ArrowLineDownAlt8)<{
    isDescending: boolean;
}>`
    position: absolute;
    bottom: -3px;
    ${props => (props.isDescending ? 'right: 0px' : 'left: 0px')};
`;

const ArrowWrapper = styled.div<{
    $hasSort: boolean;
}>`
    opacity: 0;
    margin-left: 4px;
    height: 13px;
    width: 15px;
    position: relative;

    ${({ $hasSort }) =>
        $hasSort &&
        css`
            opacity: 1;
        `}

    ${setIconFillColor(vars.colors.text.tertiary)}
`;

const Container = styled(HBClickable)<{
    isDisabled: boolean;
}>`
    display: flex;
    align-items: end;

    &:hover,
    &:focus {
        ${ArrowWrapper} {
            ${setIconFillColor(vars.colors.text.secondary)}
            opacity: 1;
        }
    }

    ${({ isDisabled }) =>
        !isDisabled &&
        css`
            cursor: pointer;
        `}
`;
